import { logError } from '@databases/logs';
import { perf } from '@databases/logs/performance';
import { proxy, snapshot } from 'valtio';

import { Deferrable } from '@canvas/deferrable';

import { defaultFeatureFlagState, FeatureFlagKey, FeatureFlags } from '../featureFlags';
import { getOverrides } from '../overrides/featureFlagPersistedOverrides';
import { processQueryParamFeatureFlags } from '../overrides/featureFlagUrlOverrides';

type Mutable<T> = { -readonly [P in keyof T]: Mutable<T[P]> };

export interface FeatureFlagClient {
  initialized: Deferrable<string>;
  initialize: () => Promise<void>;
  name: string;
  status: 'initialized' | 'errored' | 'new';
}

export class FeatureFlagClientsManager {
  clients: FeatureFlagClient[];

  constructor() {
    this.clients = [];
  }

  /**
   * Adds a client to the manager
   * @param client FeatureFlagClient
   */
  add(client: FeatureFlagClient) {
    this.clients.push(client);
  }

  /**
   * Initializes all clients
   */
  async initialize() {
    for (const client of this.clients) {
      if (client.status === 'new') {
        try {
          await client.initialize();
          await client.initialized.resolve(client.name);
          client.status = 'initialized';
          // eslint-disable-next-line canvas/no-swallow-errors-in-catch
        } catch (e) {
          client.status = 'errored';
          const error = new Error(
            `Could not initialize ${client.name} feature flags client. Reason: ${e}`
          );
          logError(error);
          await client.initialized.resolve(client.name);
        }
      }
    }

    resolvedFeatureFlagState = snapshot(currentFeatureFlagState);

    // apply persisted overrides
    this.applyOverrides();
    perf.timeLog('app-initialization', 'feature-flags-end');
  }

  applyOverrides() {
    processQueryParamFeatureFlags(window.location.href);
    const overrides = getOverrides();
    Object.entries(currentFeatureFlagState).forEach(([property]) => {
      const key = property as keyof typeof currentFeatureFlagState;
      if (overrides[key] !== undefined) {
        currentFeatureFlagState[key].enabled = overrides[key];
      } else {
        currentFeatureFlagState[key].enabled = resolvedFeatureFlagState[key].enabled;
      }
    });
  }

  /**
   * Returns a promise that resolves when all clients have been initialized
   */
  initialized(): Promise<string[]> {
    return Promise.all(this.clients.map((client) => client.initialized.get()));
  }
}

// Resolved feature flags without overrides applied.
export let resolvedFeatureFlagState: FeatureFlags = defaultFeatureFlagState;

// Resolved feature flags with overrides applied.
export const currentFeatureFlagState: Mutable<FeatureFlags> = proxy(
  defaultFeatureFlagState
);

export const getFlagValue = (key: FeatureFlagKey, defaultValue: boolean): boolean => {
  if (currentFeatureFlagState[key]) {
    return currentFeatureFlagState[key].enabled;
  }
  return defaultValue;
};

export const getFeatureFlagsInUse = (): string[] => {
  const featureFlagsInUse: string[] = [];

  for (const key in currentFeatureFlagState) {
    if (Object.hasOwn(currentFeatureFlagState, key)) {
      const flagKey = key as FeatureFlagKey;
      if (currentFeatureFlagState[flagKey].enabled) {
        featureFlagsInUse.push(flagKey);
      }
    }
  }

  return featureFlagsInUse;
};

export const featureFlagClientManager = new FeatureFlagClientsManager();
